import type { StyleTokens } from "../types";

export const COLOR_KEYS = [
  "primary",
  "secondary",
  "accent",
  "background",
  "surface",
  "foreground",
  "muted",
  "border",
] as const;

export type ColorKey = (typeof COLOR_KEYS)[number];

const DEFAULT_COLORS: Record<ColorKey, string> = {
  primary: "#0B2545",
  secondary: "#E2E8F0",
  accent: "#8DA9C4",
  background: "#F8FAFC",
  surface: "#FFFFFF",
  foreground: "#0F172A",
  muted: "#64748B",
  border: "#E2E8F0",
};

export const FONT_CHOICES = [
  "Inter",
  "DM Sans",
  "Manrope",
  "Nunito",
  "Poppins",
  "Montserrat",
  "Roboto",
  "Open Sans",
  "Lato",
  "Source Sans 3",
  "IBM Plex Sans",
  "Work Sans",
  "Raleway",
  "Space Grotesk",
  "Playfair Display",
  "Merriweather",
].map((font) => ({ id: font, name: font }));

export const RADIUS_CHOICES = [
  { id: "0rem", name: "0 (recto)" },
  { id: "0.25rem", name: "4 px" },
  { id: "0.5rem", name: "8 px" },
  { id: "0.75rem", name: "12 px" },
  { id: "0.875rem", name: "14 px" },
  { id: "1.25rem", name: "20 px" },
];

export const resolveTokens = (tokens?: StyleTokens) => ({
  colors: { ...DEFAULT_COLORS, ...(tokens?.colors ?? {}) } as Record<
    ColorKey,
    string
  >,
  fonts: {
    heading: tokens?.fonts?.heading || "Inter",
    body: tokens?.fonts?.body || tokens?.fonts?.heading || "Inter",
  },
  radius: tokens?.radius || "0.5rem",
  mode: tokens?.mode || "light",
});

/** Readable text colour on top of a background colour. */
export const contrastText = (hex: string) => {
  const value = hex.replace("#", "");
  if (value.length < 6) return "#ffffff";
  const [r, g, b] = [0, 2, 4].map(
    (i) => parseInt(value.slice(i, i + 2), 16) / 255,
  );
  const lum = [r, g, b].map((c) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4,
  );
  const luminance = 0.2126 * lum[0] + 0.7152 * lum[1] + 0.0722 * lum[2];
  return luminance > 0.45 ? "#0B0F19" : "#FFFFFF";
};

export const googleFontsHref = (fonts: string[]) => {
  const families = [...new Set(fonts.filter(Boolean))]
    .map((font) => `family=${font.replace(/ /g, "+")}:wght@400;500;600;700`)
    .join("&");
  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
};

const fontStack = (font: string) =>
  `"${font}", ui-sans-serif, system-ui, sans-serif`;

export const toCssVariables = (tokens?: StyleTokens) => {
  const t = resolveTokens(tokens);
  return [
    ":root {",
    ...COLOR_KEYS.map((key) => `  --color-${key}: ${t.colors[key]};`),
    `  --color-on-primary: ${contrastText(t.colors.primary)};`,
    `  --font-heading: ${fontStack(t.fonts.heading)};`,
    `  --font-body: ${fontStack(t.fonts.body)};`,
    `  --radius: ${t.radius};`,
    "}",
  ].join("\n");
};

/** shadcn/ui (Tailwind v4) theme, ready to paste into a new project's CSS. */
export const toShadcnTheme = (tokens?: StyleTokens) => {
  const t = resolveTokens(tokens);
  const c = t.colors;
  const vars: Record<string, string> = {
    background: c.background,
    foreground: c.foreground,
    card: c.surface,
    "card-foreground": c.foreground,
    popover: c.surface,
    "popover-foreground": c.foreground,
    primary: c.primary,
    "primary-foreground": contrastText(c.primary),
    secondary: c.secondary,
    "secondary-foreground": contrastText(c.secondary),
    muted: c.secondary,
    "muted-foreground": c.muted,
    accent: c.accent,
    "accent-foreground": contrastText(c.accent),
    destructive: "#DC2626",
    border: c.border,
    input: c.border,
    ring: c.primary,
  };
  return [
    `${t.mode === "dark" ? ".dark" : ":root"} {`,
    `  --radius: ${t.radius};`,
    ...Object.entries(vars).map(([key, value]) => `  --${key}: ${value};`),
    "}",
    "",
    "@theme inline {",
    `  --font-sans: ${fontStack(t.fonts.body)};`,
    `  --font-heading: ${fontStack(t.fonts.heading)};`,
    "}",
  ].join("\n");
};
